import React, { useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Loading,
  Message,
  toast,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ModalWithSelect } from 'components/ModalSelect'
import {
  userPermissionsFormSchema,
  UserPermissionsFormSchema,
} from '../../../schema'
import { useCreateUserPermissionsFlowContextValue } from '../context/CreateUserPermissionsFlowProvider'
import { useUserPermissionsWorkflowsContextValue } from '../../../context/UserPermissionsWorkflowsProvider'
import { UserPicker } from '../../../components/UserPicker'
import { PermissionsChecklist } from '../../../components/PermissionsChecklist'
import { CompanyScopesChecklist } from '../../../components/CompanyScopesChecklist/CompanyScopesChecklist'
import { AssignableUser } from 'api/admin/userPermissions/types'
import { useUserPermissionsQuery } from 'queries/admin/userPermissions'
import { useUserProfileById } from 'queries/user'
import { useUserPermissionsContextValue } from '../../../context/UserPermissionsProvider'

export const CreateUserPermissionsFlowMain = () => {
  const {
    createUserPermissionsFlow: { isOpen, close },
  } = useUserPermissionsWorkflowsContextValue()

  const {
    state: {
      userId: filteredUserId,
      items: filteredItems,
      manageablePermissionOptions,
      canManageCompanyScopes,
    },
  } = useUserPermissionsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateUserPermissionsFlowContextValue()

  const grantablePermissionSet = useMemo(
    () => new Set(manageablePermissionOptions.map((option) => option.value)),
    [manageablePermissionOptions]
  )

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UserPermissionsFormSchema>({
    resolver: zodResolver(userPermissionsFormSchema),
    defaultValues: {
      userId: undefined,
      name: '',
      email: '',
      permissions: [],
      companyScopes: [],
    },
  })

  const userId = watch('userId')
  const name = watch('name')
  const email = watch('email')
  const selectedPermissions = watch('permissions')
  const selectedUser: AssignableUser | null =
    userId && name ? { userId, name, email } : null

  const { data: filteredUserProfile } = useUserProfileById(
    isOpen ? filteredUserId : null
  )

  useEffect(() => {
    if (!isOpen || filteredUserId == null) {
      return
    }

    // Don't override if the operator already picked a different user in this session.
    if (userId != null && userId !== filteredUserId) {
      return
    }

    const nameFromList = filteredItems.find(
      (item) => item.id === filteredUserId
    )?.name
    const nextName =
      filteredUserProfile?.name ?? nameFromList ?? `User ${filteredUserId}`
    const nextEmail = filteredUserProfile?.email ?? ''

    setValue('userId', filteredUserId, { shouldValidate: true })
    setValue('name', nextName, { shouldValidate: true })
    setValue('email', nextEmail, { shouldValidate: true })
    if (userId !== filteredUserId) {
      setValue('permissions', [])
      setValue('companyScopes', [])
    }
  }, [
    isOpen,
    filteredUserId,
    filteredUserProfile,
    filteredItems,
    userId,
    setValue,
  ])

  const { data: existingGrantsData, isPending: isLoadingExistingGrants } =
    useUserPermissionsQuery(
      { userId, pageSize: 50 },
      { enabled: Boolean(userId) }
    )

  const existingUser = useMemo(() => {
    const users = existingGrantsData?.pages.flatMap((page) => page.users) ?? []
    return users.find((user) => user.id === userId)
  }, [existingGrantsData, userId])

  const disabledPermissions = useMemo(() => {
    return (existingUser?.permissions ?? [])
      .map((grant) => grant.permission)
      .filter((permission) => grantablePermissionSet.has(permission))
  }, [existingUser, grantablePermissionSet])

  const existingCompanyScopes = useMemo(() => {
    return (existingUser?.companyScopes ?? []).map((scope) => ({
      allCompanies: scope.allCompanies,
      companyId: scope.company?.id ?? null,
      companyGroupId: scope.companyGroup?.id ?? null,
    }))
  }, [existingUser])

  useEffect(() => {
    if (disabledPermissions.length === 0) {
      return
    }
    const disabledSet = new Set(disabledPermissions)
    const next = selectedPermissions.filter(
      (item) => !disabledSet.has(item.permission)
    )
    if (next.length !== selectedPermissions.length) {
      setValue('permissions', next)
    }
  }, [disabledPermissions, selectedPermissions, setValue])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        userId: data.userId,
        permissions: data.permissions,
        companyScopes: canManageCompanyScopes
          ? data.companyScopes.map((scope) => ({
              companyId: scope.scopeType === 'company' ? scope.companyId : null,
              companyGroupId:
                scope.scopeType === 'company_group'
                  ? scope.companyGroupId
                  : null,
              allCompanies: scope.scopeType === 'all_companies',
              effectiveAt: scope.effectiveAt,
              expiresAt: scope.expiresAt,
            }))
          : [],
      },
      {
        onSuccess: () => {
          const grantedPermissions = data.permissions.length > 0
          const grantedScopes =
            canManageCompanyScopes && data.companyScopes.length > 0
          const message =
            grantedPermissions && grantedScopes
              ? 'Permissions and company scopes granted successfully'
              : grantedScopes
                ? 'Company scopes granted successfully'
                : 'Permissions granted successfully'
          toast.success(message, {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Grant permissions
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Grant user permissions
        </Heading>

        <Box mt={4}>
          <UserPicker
            value={userId}
            selectedUser={selectedUser}
            error={errors.userId?.message?.toString()}
            onChange={(user) => {
              if (!user) {
                setValue('userId', undefined as unknown as number, {
                  shouldValidate: true,
                })
                setValue('name', '')
                setValue('email', '')
                setValue('permissions', [])
                setValue('companyScopes', [])
                return
              }
              setValue('userId', user.userId, { shouldValidate: true })
              setValue('name', user.name, { shouldValidate: true })
              setValue('email', user.email, { shouldValidate: true })
              setValue('permissions', [])
              setValue('companyScopes', [])
            }}
          />
        </Box>

        <Box mt={4}>
          {!userId ? (
            <Message variant="info" borderRadius="small">
              Select a user to choose permissions
              {canManageCompanyScopes ? ' and company scopes' : ''}.
            </Message>
          ) : isLoadingExistingGrants ? (
            <Loading />
          ) : (
            <Flex flexDirection="column" gap={4}>
              <Controller
                control={control}
                name="permissions"
                render={({ field }) => (
                  <PermissionsChecklist
                    options={manageablePermissionOptions}
                    value={field.value}
                    onChange={field.onChange}
                    disabledPermissions={disabledPermissions}
                    errors={errors.permissions}
                  />
                )}
              />
              {canManageCompanyScopes ? (
                <Controller
                  control={control}
                  name="companyScopes"
                  render={({ field }) => (
                    <CompanyScopesChecklist
                      value={field.value}
                      onChange={field.onChange}
                      existingScopes={existingCompanyScopes}
                      errors={errors.companyScopes}
                    />
                  )}
                />
              ) : null}
              {typeof errors.permissions?.message === 'string' ? (
                <Message variant="error" borderRadius="small">
                  {errors.permissions.message}
                </Message>
              ) : null}
            </Flex>
          )}
        </Box>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
